import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    TextField,
} from '@mui/material';
import { NumericFormat } from 'react-number-format';
import api from '../../api';
import HtmlColorField from '../HtmlColorField';
import { parseFormattedMoneyToCents } from '../../helpers/formatters';
import { isHtmlHexColor, normalizeHtmlHex } from '../../helpers/htmlColor';
import { DEFAULT_SPOOL_WEIGHT_GRAMS } from '../../helpers/filaments';

/**
 * Cadastro rápido de filamento a partir do formulário de impressão, para o caso
 * de o rolo usado ainda não estar cadastrado. Devolve o registro criado para que
 * a linha já fique selecionada.
 */
function QuickFilamentDialog({ open, onClose, onCreated }) {
    const [type, setType] = useState('PLA');
    const [brand, setBrand] = useState('');
    const [color, setColor] = useState('#000000');
    const [colorLabel, setColorLabel] = useState('');
    const [spoolWeight, setSpoolWeight] = useState(String(DEFAULT_SPOOL_WEIGHT_GRAMS));
    const [currentWeight, setCurrentWeight] = useState(String(DEFAULT_SPOOL_WEIGHT_GRAMS));
    const [cost, setCost] = useState('');
    const [costFloat, setCostFloat] = useState(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    const reset = () => {
        setType('PLA');
        setBrand('');
        setColor('#000000');
        setColorLabel('');
        setSpoolWeight(String(DEFAULT_SPOOL_WEIGHT_GRAMS));
        setCurrentWeight(String(DEFAULT_SPOOL_WEIGHT_GRAMS));
        setCost('');
        setCostFloat(undefined);
        setError('');
    };

    const handleClose = () => {
        if (isSubmitting) {
            return;
        }
        reset();
        onClose();
    };

    const handleSubmit = () => {
        if (isSubmitting) {
            return;
        }

        if (!type.trim() || !brand.trim() || !colorLabel.trim()) {
            setError('Informe o tipo, a marca e o nome da cor.');
            return;
        }
        if (!isHtmlHexColor(color)) {
            setError('Selecione a cor no seletor.');
            return;
        }
        const spoolGrams = parseInt(spoolWeight, 10);
        const currentGrams = currentWeight === '' ? spoolGrams : parseInt(currentWeight, 10);
        if (!Number.isFinite(spoolGrams) || spoolGrams <= 0) {
            setError('Informe o peso do carretel.');
            return;
        }
        if (!Number.isFinite(currentGrams) || currentGrams < 0 || currentGrams > spoolGrams) {
            setError('O peso atual deve ser entre zero e o peso do carretel.');
            return;
        }

        setIsSubmitting(true);
        setError('');
        api.post('filaments', {
            type: type.trim(),
            brand: brand.trim(),
            color: normalizeHtmlHex(color),
            color_label: colorLabel.trim(),
            spool_weight: spoolGrams,
            current_weight: currentGrams,
            cost: parseFormattedMoneyToCents(costFloat, cost),
        })
            .then((response) => {
                const created = response.data?.data;
                if (!created?.uuid) {
                    setError('O filamento foi criado, mas não foi possível selecioná-lo. Recarregue a lista.');
                    return;
                }
                reset();
                onCreated(created);
            })
            .catch((err) => {
                setError(
                    err?.response?.data?.error
                    || 'Não foi possível cadastrar o filamento agora.',
                );
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <Dialog open={Boolean(open)} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Cadastrar filamento</DialogTitle>
            <DialogContent dividers>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Tipo"
                            fullWidth
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Marca"
                            fullWidth
                            value={brand}
                            onChange={(e) => setBrand(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <HtmlColorField label="Cor (hex)" value={color} onChange={setColor} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            label="Nome da cor"
                            fullWidth
                            value={colorLabel}
                            onChange={(e) => setColorLabel(e.target.value)}
                            placeholder="Ex.: Verde menta"
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Peso do carretel (g)"
                            fullWidth
                            type="number"
                            value={spoolWeight}
                            onChange={(e) => {
                                setSpoolWeight(e.target.value);
                                setCurrentWeight(e.target.value);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Peso atual (g)"
                            fullWidth
                            type="number"
                            value={currentWeight}
                            onChange={(e) => setCurrentWeight(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <NumericFormat
                            label="Custo por kilo"
                            fullWidth
                            customInput={TextField}
                            value={cost}
                            onValueChange={(values) => {
                                setCost(values.value);
                                setCostFloat(values.floatValue);
                            }}
                            thousandSeparator="."
                            decimalSeparator=","
                            prefix="R$ "
                            decimalScale={2}
                            fixedDecimalScale
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Alert severity="info">
                        Você pode completar os demais dados depois, na tela de filamentos.
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting}>Cancelar</Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    startIcon={isSubmitting && <CircularProgress size={18} color="inherit" />}
                >
                    {isSubmitting ? 'Cadastrando...' : 'Cadastrar e usar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default QuickFilamentDialog;
